import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw, Network } from 'lucide-react';
import { Button } from '../ui/Button';
import { rmmService, RmmCustomerReconciliation } from '../../services/rmmService';
import { NinjaDeviceSyncStatus } from '../integrations/NinjaDeviceSyncStatus';
import { devicePullSummary, latestApiInstant } from '../../services/rmmSyncSchedule';
import { RmmDeviceStatusPanel } from './RmmDeviceStatusPanel';

interface Props {
  partnerId: string;
  customerId: string;
  customerName?: string;
}

/**
 * NinjaRMM device reconciliation for one customer. Renders nothing when the
 * partner has no active NinjaOne connection; shows a mapping hint when connected
 * but this customer has no confirmed org mapping.
 */
export function RmmReconciliationCard({ partnerId, customerId, customerName }: Props) {
  const [recon, setRecon] = useState<RmmCustomerReconciliation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [pulledAt, setPulledAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!partnerId || !customerId) return;
    setLoading(true);
    setError(null);
    try {
      setRecon(await rmmService.getCustomerReconciliation(partnerId, customerId));
    } catch (e: any) {
      setError(e?.message || 'Failed to load RMM reconciliation');
    } finally {
      setLoading(false);
    }
  }, [partnerId, customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncMessage(null);
    try {
      const result = await rmmService.sync(partnerId);
      if (!result.success) {
        setError(result.error || 'Sync failed');
        return;
      }
      setSyncMessage(devicePullSummary(result.deviceCount, result.organizationCount));
      setPulledAt(result.syncedAt);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    try {
      await rmmService.downloadCustomerReconciliationCsv(partnerId, customerId, customerName);
    } catch (e: any) {
      setError(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Not connected (or still establishing that): take no space on the page.
  if (loading && !recon) return null;
  if (!recon || !recon.connected) return null;

  const c = recon.customer;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-orange-600" />
          <h3 className="text-base font-semibold text-slate-900">NinjaRMM Reconciliation</h3>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Reload this report" title="Reload this report">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {recon.mapped && !c?.error && (
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
              <Download className="h-4 w-4 mr-1" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      <div className="mb-4">
        <NinjaDeviceSyncStatus
          compact
          lastSyncedAt={latestApiInstant(recon.lastSyncedAt, pulledAt)}
          syncing={syncing}
          syncMessage={syncMessage}
          onSync={handleSync}
        />
      </div>

      {!recon.mapped ? (
        <p className="text-sm text-slate-500">
          NinjaRMM is connected, but this customer isn't mapped to a NinjaOne organization yet.{' '}
          <Link to="/integrations/ninjaone" className="text-orange-600 hover:text-orange-700 font-medium">
            Map it on the Organizations tab →
          </Link>
        </p>
      ) : c?.error ? (
        <p className="text-sm text-rose-600">{c.error}</p>
      ) : c ? (
        <>
          <RmmDeviceStatusPanel c={c} />
          <div className="text-xs text-slate-400 mt-3">
            Ninja org: {c.externalOrgName}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default RmmReconciliationCard;
